import { FormattedMessage, useIntl } from 'gatsby-plugin-intl';
import * as React from 'react';
import { Col, Grid, Row } from 'react-styled-flexboxgrid';

import { Content } from '../components/common/Content';
import { Indent } from '../components/common/Indent';
import { Layout } from '../components/common/Layout';
import { SEO } from '../components/common/Seo';
import { StyledText } from '../components/common/StyledText';
import { TitleLine } from '../components/common/TitleLine';

const TermsPage = () => {
  const intl = useIntl();
  return (
    <Layout>
      <SEO title={intl.formatMessage({ id: 'seo.terms.title' })} />
      <Content>
        <Indent bottom={4.5} top={2} width='100%'>
          <Grid>
            <Row>
              <Col xs={12}>
                <Indent bottom={3}>
                  <h2>
                    <StyledText
                      color='textInverted'
                      size='1.8'
                      smSize='2.25'
                      xsAlign='center'
                      smAlign='left'
                      type='inline-block'>
                      <FormattedMessage id='terms.title' />
                    </StyledText>
                  </h2>

                  <TitleLine />
                </Indent>
              </Col>
            </Row>
            <Row>
              <Col xs={12}>
                <h1>AFFILIATE AGREEMENT</h1>
                <p>
                  This Affiliate Agreement (the “Agreement”) is entered by and between ExpertOption Ltd., a company
                  registered in St. Vincent and the Grenadines, with its registered address located at: Suite 305,
                  Griffith Corporate Centre, Beachmont, P.O.Box 1510, Kingstown, Saint Vincent and the Grenadines
                  (hereinafter referred to as the “Company”) and a natural or legal person, who has opened a partner
                  account with the Company (hereinafter referred to as the “Partner”). The Company and the Partner are
                  sometimes referred to herein individually as a “Party” and collectively as the “Parties”.
                </p>
                <p>
                  By submitting the application form for a partner account opening, the Partner agrees and confirms that
                  all terms and conditions of this Agreement have been read, understood and accepted in full.
                </p>
                <p>This Agreement comes into force on the date when a partner account is opened.</p>

                <h2>Subject of the Agreement</h2>

                <p>
                  In accordance with the terms and conditions of this Agreement the Partner undertakes the
                  responsibility to search and attract clients to open trading accounts with the Company.
                </p>
                <p>
                  The Company undertakes the responsibility to pay for the Partner's services on the terms and
                  conditions set out in this Agreement.
                </p>

                <h2>Partner Account Registration</h2>

                <p>
                  In order to open a partner account with the Company, the Partner has to complete and submit an online
                  application at:{' '}
                  <a href={`https://${BASE_DOMAIN}/`} target='_blank'>
                    {BASE_DOMAIN}
                  </a>
                </p>
                <p>
                  All partner accounts are denominated in USD, therefore all commissions are accrued and paid out in
                  USD.
                </p>
                <p>
                  At its sole discretion, the Company reserves the right to reject the Partner’s application for any
                  reason and without providing any further detail as to the reason(s) of the application rejection.
                </p>
                <p>
                  Natural or legal persons being citizens or residents of any of the sanctioned countries are not
                  eligible to participate in the Affiliate Program of the Company.
                </p>

                <h2>Partner’s Responsibilities</h2>

                <h3>The Partner has the responsibility to:</h3>
                <ul>
                  <li>
                    provide reliable, accurate and relevant personal details in the application form, as well as copies
                    of the required identification documents;
                  </li>
                  <li>
                    immediately notify the Company in the event of any changes in the personal data previously provided;
                  </li>
                  <li>have knowledge about financial markets in general and services provided by the Company;</li>
                  <li>ensure confidentiality of all information obtained through the Affiliate Program;</li>
                  <li>
                    immediately inform the Company about all the facts or circumstances that become known to the Partner
                    and may lead to adverse effects to the Company’s reputation or financial stability;
                  </li>
                  <li>
                    promptly provide the Company with any additional information or documents that might be required in
                    order to comply with applicable laws and regulations.
                  </li>
                </ul>

                <h2>Company’s Rights and Responsibilities</h2>

                <h3>The Company has the right to:</h3>
                <ul>
                  <li>monitor the Partner's activities and marketing methods;</li>
                  <li>at its sole discretion accept or decline any client referred by the Partner;</li>
                  <li>
                    cancel completely or partially the Partner's commissions for transactions performed by the clients
                    referred through the affiliate program if:
                    <ol type='a'>
                      <li>referred clients requested chargebacks from the Company;</li>
                      <li>the quality of the traffic directed by the Partner is not high;</li>
                      <li>
                        trading accounts of the referred clients have been blocked due to the violations of the terms
                        and conditions of the Client Agreement;
                      </li>
                      <li>
                        the Company bears the excess costs and expenses (payment for the traffic, settlement of tracking
                        systems fees, etc.) related to the low quality traffic directed by the Partner.
                      </li>
                    </ol>
                  </li>
                  <li>
                    close the partner account and cancel all commissions accrued if:
                    <ol type='a'>
                      <li>
                        the Company suspects or has reasons to believe that a referred client has abused the trading
                        account or acted in bad faith;
                      </li>
                      <li>
                        the Company, at its sole discretion, determines that the Partner uses the partner account for
                        any illegal, fraudulent or suspicious schemes;
                      </li>
                      <li>the Partner violates the terms and conditions of this Agreement.</li>
                    </ol>
                  </li>
                </ul>

                <h3>The Company has the responsibility to:</h3>
                <ul>
                  <li>provide comprehensive information support to the Partner;</li>
                  <li>
                    open trading accounts with the Company for the clients referred by the Partner in accordance with
                    the Company’s Client Acceptance Policy;
                  </li>
                  <li>
                    to pay for the Partner’s services in accordance with the terms and conditions of this Agreement;
                  </li>
                  <li>promptly inform the Partner if there are any significant changes in the Company’s operations;</li>
                  <li>
                    ensure that the Partner’s personal data has been collected, processed and held in accordance with
                    applicable Data Protection laws.
                  </li>
                </ul>

                <h2>Limitation of Liability</h2>
                <p>Under no circumstances will the Company be held liable for:</p>
                <ul>
                  <li>
                    any act, committed by the Partner, that violates the terms and conditions of this Agreement or
                    exceeds the authority granted by the Company to the Partner under this Agreement;
                  </li>
                  <li>any immaterial (moral) damages incurred by the Partner;</li>
                  <li>any damages or losses caused by the Partner to any third party;</li>
                  <li>any advice or recommendation provided by the Partner to the referred client;</li>
                  <li>any costs or charges related to marketing or promotions initiated by the Partner.</li>
                </ul>

                <h2>Commissions and Payment Terms</h2>
                <p>The Partner will be entitled to receive the commissions only if:</p>
                <ul>
                  <li>
                    the referred clients have been verified by the Company and real trading accounts have been opened
                    with the Company;
                  </li>
                  <li>the Partner has referred at least 5 active clients (for the revenue share model);</li>
                  <li>
                    the Partner has referred at least 10 active clients (for the cost per action first time deposit
                    model).
                  </li>
                </ul>

                <p>
                  Commission defined by this Agreement is the only type of commission the Partner is entitled to receive
                  and the Partner cannot claim any other commission unless otherwise agreed between the Parties in
                  writing.
                </p>
                <p>The Partner’s commission is calculated and accrued based on one of the below described models:</p>

                <h3>Revenue Sharing Model</h3>
                <p>
                  The Partner’s commission might constitute up to 60% from the Company’s revenue generated by the
                  referred clients for the life of their trading accounts.
                </p>
                <p>
                  The Company, at its sole discretion, reserves the right to recalculate or withhold the Partner's
                  commissions from the day of a partner account opening due to low quality leads. The Partner
                  acknowledges and accepts that such actions shall not give rise to any claim whatsoever against the
                  Company.
                </p>

                <h3>Cost per Action (CPA) Model with fixed commission</h3>
                <p>
                  If the Partner’s commission is paid on the basis of the Cost per Action (CPA) Fixed Model related to
                  the first time deposit of the referred client, then the Company will pay a fixed amount, which shall
                  be individually agreed upon between the Company and each Partner, for each first time deposit by the
                  referred client.
                </p>

                <p>Commission under this model will be paid only if the following criteria are met:</p>
                <ul>
                  <li>the referred client opens a real account with the Company and actively trades;</li>
                  <li>
                    at the time of commission payment at least 50% of all clients’ accounts referred by the Partner
                    should be verified;
                  </li>
                  <li>
                    on average, each referred client must have a good trading history (not only deposit and withdrawal
                    transactions).
                  </li>
                </ul>
                <p>
                  The Company, at its sole discretion, reserves the right to recalculate or withhold the Partner's
                  commissions from the day of a partner account opening due to low quality leads. The Partner
                  acknowledges and accepts that such actions shall not give rise to any claim whatsoever against the
                  Company.
                </p>

                <h3>Cost per Action (CPA) 100% Model</h3>
                <p>
                  If the Partner’s commission is paid based on the Cost per Action (CPA) 100% Model related to the first
                  time deposit of the referred client, then the Company will pay out the full amount of the first time
                  deposit not exceeding $1000. If the referred client’s first time deposit exceeds $1000, the Partner’s
                  commission will be still fixed at $1000.
                </p>

                <p>Commissions under this model will be paid if the following criteria are met:</p>
                <ul>
                  <li>the referred client opens a real account with the Company and actively trades;</li>
                  <li>
                    at the time of commission payment at least 80% of all clients’ accounts referred by the Partner
                    should be verified;
                  </li>
                  <li>on average, each referred client has to make at least two deposits;</li>
                  <li>
                    on average, each referred client must have a good trading history (not only deposit and withdrawal
                    transactions);
                  </li>
                  <li>
                    on average, each first time deposit should not be significantly higher than the total amount of the
                    following deposits.
                  </li>
                </ul>

                <p>Minimum amount of the commission the Partner is allowed to withdraw is $10</p>
                <p>
                  If the Partner will request the Company to pay out the accrued commission that is less than $1000, the
                  wire transfer fee in the amount of $60 will be automatically deducted from the Partner’s account.
                </p>
                <p>Generally, Partners are paid twice a month:</p>
                <ul>
                  <li>within 10 working days after the 15th of each month for the first half of the month;</li>
                  <li>by the 10th of the following month for the second half of the month;</li>
                </ul>

                <p>
                  All payments are made in USD only, and the Company is responsible to cover all transfer fees if the
                  commission amount exceeds $1000.
                </p>
                <p>
                  The Partner has the sole responsibility to ensure that the payment details provided to the Company are
                  correct and up-to-date.
                </p>
                <p>
                  The Company can also pay out the Partner’s commission if a relevant withdrawal request is sent by the
                  Partner to the Company. In such cases withdrawal requests will be approved by the Company only if the
                  Partner’s commission is more than $500.
                </p>
                <p>
                  Partners who have been removed from the Affiliate Program by the Company or who have voluntarily
                  closed their partner accounts will no longer receive commissions from any referred client.
                </p>
                <p>
                  The Company reserves the right to modify the payment terms at any time without any prior consent from
                  the Partner by informing the Partner 3 working days before any amendments come into force.
                </p>
                <p>
                  The Company, at its sole discretion, reserves the right to recalculate or withhold the Partner's
                  commissions from the day of a partner account opening due to low quality leads. The Partner
                  acknowledges and accepts that such actions shall not give rise to any claim whatsoever against the
                  Company.
                </p>

                <h2>Amendment</h2>
                <p>
                  This Agreement may be amended by the Company from time to time. In such event, the Company will notify
                  the Partner by email.
                </p>
                <p>
                  Any changes to this Agreement will apply to all transactions performed after the date on which the
                  changes become effective.
                </p>
                <p>
                  If the Partner does not wish to accept the changes made by the Company, the Partner has the right to
                  terminate this Agreement.
                </p>

                <h2>Termination</h2>
                <p>
                  This Agreement may be terminated by either Party by notifying the other Party in writing no less than
                  five business days prior to the termination.
                </p>
                <p>
                  The Company reserves the right to terminate this Agreement if the Partner refers less than 10 clients
                  within 12 months from the date of the partner account opening.
                </p>
                <p>
                  Upon termination of this Agreement, the Partner must return to the Company all materials used to
                  promote the Company’s business. Furthermore, upon termination of this Agreement the Partner must
                  immediately remove any Company’s materials available on the Partner’s website (if any).
                </p>
                <p>
                  Upon termination of this Agreement the Company must pay the Partner any commissions due, provided that
                  the Partner’s commissions have been generated in accordance with the terms and conditions of this
                  Agreement.
                </p>
                <p>
                  The Company reserves the right to immediately terminate this Agreement if the Partner has breached the
                  terms and conditions of this Agreement.
                </p>

                <h2>Communication</h2>
                <p>
                  The Partner must send any communication to the Company in writing by e-mail:{' '}
                  <a href='mailto:partner@expertoption.com'>partner@expertoption.com</a>
                </p>
                <p>All communication between the Company and the Partner must be in English.</p>

                <h2>Marketing and Promotion Restrictions</h2>
                <p>When marketing and promoting the Company’s services, the Partner is prohibited:</p>
                <ul>
                  <li>to use spam or bulk emails;</li>
                  <li>to use unsolicited commercial emails;</li>
                  <li>to provide false or misleading information in order to attract more clients;</li>
                  <li>
                    to utilize the Partner’s link to refer their own customer account(s) to receive commission (“self
                    referrals”);
                  </li>
                  <li>
                    to use contextual advertising for the ExpertOption brand, related words or misspellings in search
                    engines, such as Google, Yandex, Bing, Yahoo, Baidu
                  </li>
                </ul>

                <h2>Disputes and Complaints</h2>
                <p>
                  Should any dispute or disagreement arise in connection with this Agreement, the Parties shall exert
                  every effort to settle such disputes and disagreements through negotiations.
                </p>
                <p>
                  All complaints must be sent by the Partner to the Company using the following email:
                  partner@expertoption.com
                </p>
                <p>
                  All received complaints shall be acknowledged and investigated by the Company within 10 working days.
                </p>
                <p>
                  Complaints about the terms and conditions of the Company’s Affiliate Program will be neither accepted
                  nor investigated.
                </p>

                <h2>Applicable Law</h2>
                <p>This Agreement is concluded in accordance with the laws of Saint Vincent and the Grenadines.</p>
                <p>
                  All disputes or disagreements not settled by the Parties through negotiations shall be referred to
                  arbitration in Saint Vincent and the Grenadines, unless otherwise agreed by the Parties.
                </p>

                <h2>Miscellaneous</h2>
                <p>This Agreement is an electronic document and does not require to be signed by the Parties.</p>
                <p>
                  The Partners is an independent contractor, and nothing in this Agreement will create any partnership,
                  joint venture, agency, franchise, sales representative, or employment relationship between the Partner
                  and the Company.{' '}
                </p>
                <p>
                  The Partner does not have any authority to make or accept any offers or representations on behalf of
                  the Company.
                </p>
                <p>
                  This Agreement represents the entire agreement between the Company and the Partner, and shall
                  supersede all prior oral or written agreements and communications between the Parties.
                </p>
                <p>
                  The section headings of this Agreement are for convenience of reference only and shall not be deemed
                  to alter or affect any provision hereof.
                </p>
              </Col>
            </Row>
          </Grid>
        </Indent>
      </Content>
    </Layout>
  );
};

export default TermsPage;
